import { apiClient } from './client';
import {
  ReportRequest,
  ReportRequestCreate,
  ReportFilters,
} from '../../types/api';

const BASE_URL = '/reports';

export const reportService = {
  async createReport(data: ReportRequestCreate): Promise<ReportRequest> {
    const response = await apiClient.post(`${BASE_URL}/`, data);
    return response.data;
  },

  async getReports(filters?: ReportFilters): Promise<ReportRequest[]> {
    const params = new URLSearchParams();
    if (filters?.report_type) params.append('report_type', filters.report_type);
    if (filters?.status_filter) params.append('status_filter', filters.status_filter);
    if (filters?.skip) params.append('skip', filters.skip.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await apiClient.get(`${BASE_URL}/?${params.toString()}`);
    return response.data;
  },

  async getReport(id: string): Promise<ReportRequest> {
    const response = await apiClient.get(`${BASE_URL}/${id}`);
    return response.data;
  },

  async downloadReport(id: string): Promise<Blob> {
    const response = await apiClient.get(`${BASE_URL}/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  async deleteReport(id: string): Promise<void> {
    await apiClient.delete(`${BASE_URL}/${id}`);
  },

  async regenerateReport(id: string): Promise<ReportRequest> {
    const response = await apiClient.post(`${BASE_URL}/${id}/regenerate`);
    return response.data;
  },

  /**
   * Generate a *draft* term-report narrative via the local LLM.
   *
   * Long-running (10-60s) -- caller should treat as a deliberate user
   * action, not a query. Returns the structured narrative plus the
   * source observations so the UI can render citations.
   */
  async draftNarrative(req: DraftNarrativeRequest): Promise<ReportNarrative> {
    const response = await apiClient.post(`${BASE_URL}/draft-narrative`, req);
    return response.data;
  },
};

// ── AI draft-narrative types ───────────────────────────────────────────

export interface DraftNarrativeRequest {
  child_id: string;
  /** ISO datetime; the API enforces a max ~12-month window. */
  date_from: string;
  date_to: string;
}

export interface ReportNarrativeSection {
  area_id: string;
  area_name: string;
  narrative: string;
  /** Observation IDs the LLM cited as evidence. The server has filtered
   * these against the input set, so the UI can trust them. */
  evidence_ids: string[];
}

export interface ReportNarrativeNextStep {
  text: string;
  area_id?: string | null;
}

export interface ObservationCitation {
  id: string;
  observation_date: string;
  learning_area_id?: string | null;
  learning_area_name?: string | null;
  excerpt: string;
}

export interface ReportNarrative {
  child_id: string;
  child_name: string;
  period_start: string;
  period_end: string;
  summary: string;
  sections: ReportNarrativeSection[];
  next_steps: ReportNarrativeNextStep[];
  /** Source data echoed back so the citation UI doesn't need a follow-up
   * fetch per chip. Keyed by id for quick lookup in the viewer. */
  observations: ObservationCitation[];
  observations_considered: number;
  assessments_considered: number;
}
